import { INTENSITY } from '../config/gameConfig';

export type IntensityPhase = 'cruise' | 'rush' | 'final';

export interface IntensitySnapshot {
  heat: number;
  heatRatio: number;
  multiplier: number;
  phase: IntensityPhase;
  rushActive: boolean;
  finalStretch: boolean;
  styleStreak: number;
  banner: string | null;
}

/**
 * Competitive pacing layer: heat, Rush Hour waves, Final Stretch.
 * Keeps early game readable and late game high-stakes.
 */
export class IntensitySystem {
  private heat = 0;
  private styleStreak = 0;
  private elapsedSec = 0;
  private rushUntil = -1;
  private nextRushAt: number = INTENSITY.firstRushAtSec;
  private banner: string | null = null;
  private bannerTtl = 0;
  private heatScoreBonus: number;

  constructor(heatScoreBonus = 0.75) {
    this.heatScoreBonus = heatScoreBonus;
  }

  reset(): void {
    this.heat = 0;
    this.styleStreak = 0;
    this.elapsedSec = 0;
    this.rushUntil = -1;
    this.nextRushAt = INTENSITY.firstRushAtSec;
    this.banner = null;
    this.bannerTtl = 0;
  }

  get heatValue(): number {
    return this.heat;
  }

  get multiplier(): number {
    return 1 + (this.heat / INTENSITY.heatMax) * this.heatScoreBonus;
  }

  get isRush(): boolean {
    return this.elapsedSec < this.rushUntil;
  }

  update(deltaMs: number, timeLeftSec: number, _durationSec: number): void {
    this.elapsedSec += deltaMs / 1000;
    this.heat = Math.max(0, this.heat - (INTENSITY.heatDecayPerSec * deltaMs) / 1000);

    if (this.bannerTtl > 0) {
      this.bannerTtl -= deltaMs / 1000;
      if (this.bannerTtl <= 0) this.banner = null;
    }

    // Schedule Rush Hour waves before final stretch.
    if (
      timeLeftSec > INTENSITY.finalStretchSec + 1 &&
      this.elapsedSec >= this.nextRushAt &&
      !this.isRush
    ) {
      this.rushUntil = this.elapsedSec + INTENSITY.rushDurationSec;
      this.nextRushAt = this.elapsedSec + INTENSITY.rushIntervalSec;
      this.showBanner('RUSH HOUR!');
    }
  }

  /** Call each frame with timeLeft to detect final stretch entry. */
  checkFinalStretch(timeLeftSec: number, wasFinal: boolean): boolean {
    const nowFinal = timeLeftSec <= INTENSITY.finalStretchSec;
    if (nowFinal && !wasFinal) {
      this.showBanner('FINAL STRETCH!');
      this.heat = Math.min(INTENSITY.heatMax, this.heat + 18);
    }
    return nowFinal;
  }

  addFareHeat(): void {
    this.addHeat(INTENSITY.fareHeat);
  }

  addNearMissHeat(): { styleBonus: number } {
    this.addHeat(INTENSITY.nearMissHeat);
    this.styleStreak += 1;
    if (this.styleStreak >= 3) {
      // style streak handled by scoring via returned flag
    }
    return { styleBonus: this.styleStreak };
  }

  breakStyle(): void {
    this.styleStreak = 0;
    this.addHeat(-INTENSITY.crashHeatPenalty);
  }

  resetStyleOnly(): void {
    this.styleStreak = 0;
  }

  snapshot(finalStretch: boolean, heatScoreBonus = this.heatScoreBonus): IntensitySnapshot {
    const phase: IntensityPhase = finalStretch ? 'final' : this.isRush ? 'rush' : 'cruise';
    return {
      heat: Math.round(this.heat),
      heatRatio: this.heat / INTENSITY.heatMax,
      multiplier: 1 + (this.heat / INTENSITY.heatMax) * heatScoreBonus,
      phase,
      rushActive: this.isRush,
      finalStretch,
      styleStreak: this.styleStreak,
      banner: this.banner,
    };
  }

  private addHeat(amount: number): void {
    this.heat = Math.max(0, Math.min(INTENSITY.heatMax, this.heat + amount));
  }

  private showBanner(text: string): void {
    this.banner = text;
    this.bannerTtl = 2.2;
  }
}
